import ErrorMessage from "@/v2-ui/ErrorMessage"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import LicenseFeatureGroupCapabilityGroup from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupCapabilityGroup"
import {
  capabilityGrantOf,
  isLockable,
  type LicenseFeatureGroupCapability,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capability.utils"
import type { FeatureGroupRuleLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import LicenseFeatureGroupAccessModeSelect from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupAccessModeSelect"
import LicenseFeatureGroupLegendTick from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupLegendTick"
import LicenseFeatureGroupSection from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupSection"
import LicenseFeatureGroupTickLegend from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupTickLegend"
import {
  CAPABILITY_FEATURE_TYPE_CODE,
  RULE_VERSUS_PICKS_LEGEND,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  isClosedForLens,
  reachesThroughLens,
  subjectOfLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import { describePackageCapabilities } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.placement.utils"
import { useFeatureSurfaceCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.surfaceCatalog.query"

export type LicenseFeatureGroupCapabilitiesSectionProps = {
  featureGroupName: string
  /** The rule draft: a module granted whole is a rule here, not a row per surface. */
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  /** The draft. */
  isPicked(code: string): boolean
  /** What is persisted; rows that differ are marked as pending changes. */
  wasPicked(code: string): boolean
  onRulesChange(rules: readonly FeatureGroupRuleLike[]): void
  onSetPicked(
    capabilities: readonly LicenseFeatureGroupCapability[],
    picked: boolean,
  ): void
}

/**
 * The catalog sectioned by module, in the order it arrives (the sidebar's
 * order, then the object panels, then the rest — sorted server-side).
 */
function groupByModule(capabilities: readonly LicenseFeatureGroupCapability[]) {
  const groups = new Map<string, LicenseFeatureGroupCapability[]>()
  for (const capability of capabilities) {
    const group = groups.get(capability.ModuleCode)
    if (group) group.push(capability)
    else groups.set(capability.ModuleCode, [capability])
  }
  return groups
}

// Everything in the app a package can switch on — the console's gated
// surfaces — as one card per module (Map, Map layers, Property, Company,
// …), each module named the way the sidebar names it and granted whole
// from its header, with the surfaces that can be carved out of it listed
// beneath. Packed into columns (CSS columns, not a grid: a one-line module
// never leaves a hole beside an eighteen-row one), so the operator reads
// the app's own structure, not a code namespace, and never needs to
// search.
function LicenseFeatureGroupCapabilitiesSection(
  props: LicenseFeatureGroupCapabilitiesSectionProps,
) {
  const {
    featureGroupName,
    rules,
    persistedRules,
    isPicked,
    wasPicked,
    onRulesChange,
    onSetPicked,
  } = props

  const { data, previousData, loading, error } = useFeatureSurfaceCatalogQuery()
  const lens = useFeatureGroupLens()
  const capabilities = (data ?? previousData)?.getFeatureSurfaceCatalog ?? []
  // What REACHES the people the lens is about: through the customer lens,
  // what their packages carry counts beside the draft.
  const reachedCount = capabilities.filter((capability) =>
    reachesThroughLens(
      lens,
      capability,
      capabilityGrantOf(capability, rules, isPicked) !== null,
    ),
  ).length
  const isClosed = isClosedForLens(lens, CAPABILITY_FEATURE_TYPE_CODE)

  function summarize() {
    if (capabilities.length === 0) {
      return "What the app can switch on per package."
    }
    if (lens.kind === "customer") {
      return isClosed
        ? `Only what's listed: ${reachedCount} of ${capabilities.length} reach ${subjectOfLens(lens)}.`
        : `Open: ${reachedCount} of ${capabilities.length} reach ${subjectOfLens(lens)}.`
    }
    // Through the package lens the count splits by what a tick means: the
    // package-only rows this package unlocks, and the free rows it places.
    return describePackageCapabilities({
      packageName: featureGroupName,
      total: capabilities.length,
      carried: reachedCount,
      carriedPackageOnly: capabilities.filter(
        (capability) =>
          isLockable(capability) &&
          capabilityGrantOf(capability, rules, isPicked) !== null,
      ).length,
    })
  }

  return (
    <LicenseFeatureGroupSection
      title="Capabilities"
      summary={summarize()}
      aside={
        <LicenseFeatureGroupAccessModeSelect
          featureTypeCode={CAPABILITY_FEATURE_TYPE_CODE}
          subject="Fusion capabilities"
        />
      }
      legend={
        isPersonLens(lens) ? (
          <>
            <LicenseFeatureGroupLegendTick /> reaches {lens.userName}; a cross
            does not. Hover either for why: free for everyone, a license, the
            customer&apos;s own list, or a seat they hold or lack.
          </>
        ) : lens.kind === "customer" ? (
          <>
            <LicenseFeatureGroupLegendTick /> included already, with the product
            or through a license (hover the tick for which). An empty box is not
            included; tick it to add it for {lens.customerName} only.{" "}
            {RULE_VERSUS_PICKS_LEGEND}
          </>
        ) : (
          <LicenseFeatureGroupTickLegend />
        )
      }
      about={
        isPersonLens(lens) ? (
          <>
            <p>
              What Fusion can do for {lens.userName}, as the app structures it:
              its modules, their tabs and tools, each with the entitlement
              rule&apos;s answer for {lens.userName} alone. The rule runs per
              customer ({lens.customerName}: what is free for everyone, what
              their licenses and add-ons include, and the customer&apos;s own
              list) and then per person, where a seated license names who holds
              a seat.
            </p>
            <p className="mt-2">
              Nothing is changed here. Licenses, add-ons and seats are set on
              the customer panel&apos;s Licenses &amp; add-ons tab; the
              customer&apos;s own list and open or listed classes on its What
              they get tab.
            </p>
          </>
        ) : lens.kind === "customer" ? (
          <>
            <p>
              What Fusion can do for {lens.customerName}, as the app structures
              it: its modules, their tabs and tools. Open (the default) means
              every capability that is free for everyone, plus what their
              licenses include, new ones arriving on their own. Only what&apos;s
              listed means exactly what their licenses include and what is
              ticked here, nothing else: a module ticked at its header is a rule
              (everything in it now, and every capability a later release adds
              to it); rows ticked one by one are only those rows.
            </p>
            <p className="mt-2">
              A fixed tick is the product&apos;s or a license&apos;s and cannot
              be changed here; change their licenses on the Licensing tab. A
              checkbox is this tab&apos;s to decide.
            </p>
          </>
        ) : (
          <>
            <p>
              What the app can do, as the app structures it: its modules, their
              tabs and tools. Each is a collection — granting it grants every
              feature inside it; hover a row for how many, and the list.
            </p>
            <p className="mt-2">
              A module ticked at its header is granted as a rule: everything in
              it now, and every capability a later release adds to it. Rows
              ticked one by one are only those rows.
            </p>
            <p className="mt-2">
              Developers define capabilities in Fusion&apos;s code: each gated
              component declares one, and every deploy&apos;s scan refreshes
              this list. Nothing is added, renamed or split here; a new one
              lands on the Feature access board, free for everyone until
              decided.
            </p>
            <p className="mt-2">
              Why a free row can be ticked: a package decides access only for a
              package-only row (the lock). Every other row is free for every
              customer today, and its tick is a placement, drawn as a dash:
              where the capability goes the day Feature access makes it
              package-only (the picker there pre-selects the packages that carry
              it). So a license reads as the complete product before anything is
              locked. Which capabilities no package carries yet is the
              &ldquo;Packages&rdquo; column on Feature access.
            </p>
          </>
        )
      }
    >
      <ErrorMessage error={error} />
      {loading && !data && !previousData ? (
        <LicenseFeatureGroupComposeSkeleton columns={3} />
      ) : (
        <div className="columns-1 gap-3 md:columns-2 xl:columns-3 [&>*]:mb-3 [&>*]:break-inside-avoid">
          {[...groupByModule(capabilities).entries()].map(([code, group]) => (
            <LicenseFeatureGroupCapabilityGroup
              key={code}
              capabilities={group}
              featureGroupName={featureGroupName}
              rules={rules}
              persistedRules={persistedRules}
              isPicked={isPicked}
              wasPicked={wasPicked}
              onRulesChange={onRulesChange}
              onSetPicked={onSetPicked}
            />
          ))}
        </div>
      )}
    </LicenseFeatureGroupSection>
  )
}

export default LicenseFeatureGroupCapabilitiesSection
